/**
 * One flat shape for an account row.
 *
 * Unlike leads, there is no legacy flat fixture to translate — `accounts` is a
 * real, fully-built module, so this reads the API's own nested shape directly
 * (`account_type.name`, `owner.full_name`, `health.band`) rather than
 * reconciling two field-name dialects.
 */

/** health.band → the prototype's tint/label pair. */
export const HEALTH_TONE = {
  Healthy: ['#ecfdf5', '#047857'],
  'At risk': ['#fffbeb', '#b45309'],
  Critical: ['#fff1f2', '#be123c'],
};

export function healthTone(band) {
  return HEALTH_TONE[band] ?? ['#f1f4fb', '#5b6478'];
}

/** lifecycle_stage → pill tone. */
export const STAGE_PILL = {
  Prospect: 'pill-sky',
  Customer: 'pill-green',
  Renewal: 'pill-violet',
  Churned: 'pill-gray',
  Dormant: 'pill-amber',
};

const LOGO_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#14b8a6', '#f43f5e', '#d946ef'];

/** A stable colour from the account's own id, so a card's logo tint never changes on refetch. */
export function logoColor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return LOGO_COLORS[hash % LOGO_COLORS.length];
}

/**
 * An API account as the flat row the views render.
 * @param {object} account
 */
export function toRow(account) {
  return {
    id: account.id,
    account_no: account.account_no,
    name: account.name,
    legalName: account.legal_name,
    parent: account.parent,
    accountType: account.account_type?.name ?? null,
    industry: account.industry?.name ?? null,
    website: account.website,
    phone: account.phone,
    email: account.email,
    employees: account.employee_count,
    revenue: account.annual_revenue,
    currency: account.currency,
    taxNumber: account.tax_number,
    registrationNumber: account.registration_number,
    billingAddress: account.billing_address,
    shippingSameAsBilling: account.shipping_same_as_billing,
    shippingAddress: account.shipping_address,
    lifecycleStage: account.lifecycle_stage,
    customerSince: account.customer_since,
    churnedOn: account.churned_on,
    segment: account.segment,
    healthScore: account.health?.score ?? null,
    healthBand: account.health?.band ?? null,
    healthComputedAt: account.health?.computed_at ?? null,
    creditLimit: account.credit_limit,
    paymentTerms: account.payment_terms,
    priceList: account.price_list,
    owner: account.owner,
    team: account.team,
    territory: account.territory,
    logoUrl: account.logo_url,
    lastActivityAt: account.last_activity_at,
    custom: account.custom,
    stats: account.stats ?? null,
    createdAt: account.created_at,
    updatedAt: account.updated_at,
    deletedAt: account.deleted_at,
    rowVersion: account.row_version,
    raw: account,
  };
}
